import { Directive, EventEmitter, Inject, Input, OnDestroy, Output, Self } from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  FormGroupDirective,
  NgForm,
  UntypedFormArray,
  UntypedFormGroup,
} from '@angular/forms';
import { isNotNull } from '@assetaccountant/core/utils/filter-nulls';
import { environment } from 'environments/environment';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

// Use this only with ValidSubmitDirective
function markFormAsTouched(form: UntypedFormGroup | UntypedFormArray) {
  const controls = isFormArray(form)
    ? form.controls
    : Object.keys(form.controls).map(k => form.controls[k]);

  controls.forEach(control => {
    control.markAsTouched();

    if (isFormGroupOrArray(control)) {
      markFormAsTouched(control);
    }
  });

  function isFormGroupOrArray(
    control: AbstractControl,
  ): control is UntypedFormGroup | UntypedFormArray {
    return 'controls' in control;
  }

  function isFormArray(control: UntypedFormGroup | UntypedFormArray): control is UntypedFormArray {
    return 'length' in control.controls;
  }
}

export interface ValidSubmitEvent {
  handle: ValidSubmitHandle;
}

export type ValidSubmitHandle = <T>(
  obs: Observable<T>,
  options?: { keepDisabledOnSuccess?: boolean },
) => Observable<T>;

interface DisabledControls {
  [key: string]: DisabledControls | boolean;
}

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'form',
  standalone: false,
})
export class ValidSubmitDirective implements OnDestroy {
  @Output() validSubmit = new EventEmitter<ValidSubmitEvent>();

  @Input() disabledIsValid = false;

  protected readonly _subscriptions: Subscription[] = [];

  constructor(@Inject(ControlContainer) @Self() form: NgForm | FormGroupDirective) {
    this._subscriptions.push(form.ngSubmit.subscribe(() => this.onSubmit(form.form)));
  }

  ngOnDestroy() {
    this._subscriptions.forEach(s => s.unsubscribe());
  }

  onSubmit(form: UntypedFormGroup | UntypedFormArray) {
    if (form.valid || (form.disabled && this.disabledIsValid)) {
      this.validSubmit.emit({
        handle: <T>(obs: Observable<T>, options?: { keepDisabledOnSuccess?: boolean }) => {
          const disabledControls = this.buildDisabledControls(form);
          form.disable({ emitEvent: false });

          return obs.pipe(
            tap({
              error: () => this.enableForm(form, disabledControls),
              complete: () => {
                if (!options?.keepDisabledOnSuccess) {
                  this.enableForm(form, disabledControls);
                }
              },
            }),
          );
        },
      });
    } else {
      if (!environment.production && !('__karma__' in window)) {
        console.warn(`Invalid form (status: ${form.status})`, form);
      }
      markFormAsTouched(form);
    }
  }

  private buildDisabledControls(form: UntypedFormGroup | UntypedFormArray): DisabledControls {
    return Object.fromEntries(
      Object.entries(form.controls)
        .map(([key, control]) => {
          if ('controls' in control) {
            return [
              key,
              this.buildDisabledControls(control as UntypedFormGroup | UntypedFormArray),
            ];
          } else if (control.disabled) {
            return [key, true];
          } else {
            return null;
          }
        })
        .filter(isNotNull),
    );
  }

  private enableForm(
    form: UntypedFormGroup | UntypedFormArray,
    disabledControls: DisabledControls,
  ) {
    form.enable({ emitEvent: false });
    Object.entries(form.controls).forEach(([key, control]) => {
      const config = disabledControls[key];
      if (config === true) {
        control.disable({ emitEvent: false });
      } else if (config) {
        this.enableForm(control as UntypedFormGroup | UntypedFormArray, config);
      }
    });
  }
}
